/**
 * The Assistant tab of the scene editor's right panel (spec 022): the scene
 * wrapper around the shared `ChatPanel`. A turn sends the current document
 * to the Service; its steps are applied as one undo step (`applyOperations`),
 * and the message offers Undo/Redo of that step.
 */
import React, { useContext, useEffect, useMemo } from 'react';
import { useStore } from 'zustand';
import { useSceneStore } from '../../../stores/sceneStore';
import { ASSISTANT_TEXT, EXAMPLE_PROMPTS, lookedLine, useAssistantStore, type AssistantMessage } from '../../../stores/assistantStore';
import { ChatAction, ChatPanel, ChatUnavailable, type ChatMessage } from '../../assistant/ChatPanel';
import type { ChatList } from '../../assistant/bites';
import { changesOfSteps, groupChanges } from '../../assistant/groupChanges';
import ChangesReport from '../../assistant/ChangesReport';
import PicturesNeededCard, { neededGroupsLine } from '../../pictures/PicturesNeededCard';
import { PictureOwnerContext } from '../../pictures/pictureOwner';
import { useOpenPictures } from '../../pictures/usePicturesNav';

const EMPTY: AssistantMessage[] = [];

/**
 * Spec 028: the Changes bite of an applied scene turn, in its one-scene form;
 * the summaries as a list from an older Service; nothing when no step
 * changed anything visible.
 */
export function sceneChangesList(m: AssistantMessage): ChatList | null {
  if (!m.changes?.length) return null;
  const plain = m.changeSteps ? changesOfSteps(m.changeSteps) : undefined;
  if (!plain) return { title: 'Changes', items: m.changes, testId: 'assistant-changes' };
  const group = groupChanges(plain);
  if (!group.cards.length) return null;
  return { title: '', items: m.changes, testId: 'assistant-changes', content: <ChangesReport group={group} form="scene" /> };
}

interface Props {
  sceneId: number | string;
  /** Spec 029: shown under the not-set-up notice (the scene studio's way to the hood). */
  unavailableExtra?: React.ReactNode;
}

/**
 * Undo while the turn is the most recent undo step (the live scene is the
 * object it produced); Redo while it is the undone step next in line (the
 * live scene is the object the undo left and the redo stack's top is the
 * turn). Any other edit removes both.
 */
const TurnActions: React.FC<{ message: AssistantMessage }> = ({ message }) => {
  const scene = useSceneStore((s) => s.scene);
  const redoTop = useStore(useSceneStore.temporal, (t) => t.futureStates[t.futureStates.length - 1]?.scene);
  if (message.appliedScene === undefined) return null;
  if (scene === message.appliedScene) {
    return <ChatAction onClick={() => useSceneStore.getState().undo()} testId="assistant-undo">Undo</ChatAction>;
  }
  if (scene === message.beforeScene && redoTop === message.appliedScene) {
    return <ChatAction onClick={() => useSceneStore.getState().redo()} testId="assistant-redo">Redo</ChatAction>;
  }
  return null;
};

export const AssistantPanel: React.FC<Props> = ({ sceneId, unavailableExtra }) => {
  const key = String(sceneId);
  // Spec 030: only the standalone scene editor (which mounts the scene's picture owner) shows the
  // Pictures needed card; a story's scene tab does not (D-11).
  const owner = useContext(PictureOwnerContext);
  const standalone = owner?.ref.kind === 'scene';
  const openPictures = useOpenPictures();
  const status = useAssistantStore((s) => s.status);
  const loadStatus = useAssistantStore((s) => s.loadStatus);
  const messages = useAssistantStore((s) => s.conversations[key] ?? EMPTY);
  const running = useAssistantStore((s) => !!s.running[key]);
  const send = useAssistantStore((s) => s.send);
  const stop = useAssistantStore((s) => s.stop);
  const retry = useAssistantStore((s) => s.retry);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  const chatMessages = useMemo(() => messages.map((m): ChatMessage => {
    const lists: ChatList[] = [];
    const changes = sceneChangesList(m);
    if (changes) lists.push(changes);
    // Spec 030: 026's Pictures needed card after the Changes bite, on the scene's own map.
    if (standalone && m.pictures && m.pictures.needed.length > 0) {
      lists.push({
        title: 'Pictures needed',
        items: [neededGroupsLine(m.pictures.needed)],
        testId: 'assistant-pictures-card',
        content: <PicturesNeededCard pictures={m.pictures} noun="scene" onOpen={() => openPictures({ filter: 'needed' })} />,
      });
    }
    return {
      id: m.id,
      role: m.role,
      text: m.text,
      ...(lists.length ? { lists } : {}),
      ...(m.retry ? { retry: true } : {}),
      ...(m.views?.length ? { looked: lookedLine(m.views) } : {}),
    };
  }), [messages, standalone, openPictures]);
  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  if (status === 'unavailable') {
    if (unavailableExtra === undefined) return <ChatUnavailable text={ASSISTANT_TEXT.unavailable} testId="assistant-unavailable" />;
    return (
      <>
        <ChatUnavailable text={ASSISTANT_TEXT.unavailable} testId="assistant-unavailable" />
        {unavailableExtra}
      </>
    );
  }

  // Spec 022 v21: the assistant works on every scene (Storyboard retired, 006).
  return (
    <ChatPanel
      key={key}
      conversationKey={key}
      messages={chatMessages}
      running={running}
      send={(text) => { void send(sceneId, text); }}
      stop={() => stop(sceneId)}
      retry={(id) => { void retry(sceneId, Number(id)); }}
      examplePrompts={EXAMPLE_PROMPTS}
      emptyHint="Describe what should happen in this scene."
      disabled={status !== 'available'}
      placeholder="e.g. Bob walks in from the left"
      renderActions={(m) => {
        const message = byId.get(Number(m.id));
        return message ? <TurnActions message={message} /> : null;
      }}
    />
  );
};

export default AssistantPanel;
